import { createContext, useContext, useEffect } from "react";

// Window titles (ticket 02): `Company · Section · detail — Growth Engine`, so
// time-tracking software can tell Companies and sections apart. Missing parts
// are dropped; a page with nothing to name reads just "Growth Engine".
export const PRODUCT_TITLE = "Growth Engine";

export interface WindowTitleParts {
  company?: string | null;
  section?: string | null;
  detail?: string | null;
}

export function formatWindowTitle({ company, section, detail }: WindowTitleParts): string {
  const place = [company, section, detail].map((part) => part?.trim()).filter(Boolean);
  return place.length ? `${place.join(" · ")} — ${PRODUCT_TITLE}` : PRODUCT_TITLE;
}

/**
 * Sets the document title while the calling component is mounted. Leaving it
 * falls back to the bare product name, so a stale place never lingers.
 */
export function useDocumentTitle(parts: WindowTitleParts) {
  const title = formatWindowTitle(parts);
  useEffect(() => {
    document.title = title;
    return () => {
      document.title = PRODUCT_TITLE;
    };
  }, [title]);
}

// A page inside the Company layout names its own detail (a draft, a report)
// through this context; the layout owns the Company and section.
export const WindowTitleDetailContext = createContext<((detail: string | null) => void) | null>(
  null,
);

// A page inside the Company layout that is not about the Company (the Settings
// sections that span all Companies) drops the Company from the title.
export const WindowTitleCompanyContext = createContext<((shown: boolean) => void) | null>(null);

/** Leaves the Company out of the window title while `general` holds. */
export function useWindowTitleGeneral(general: boolean) {
  const setCompanyShown = useContext(WindowTitleCompanyContext);
  useEffect(() => {
    if (!setCompanyShown) return;
    setCompanyShown(!general);
    return () => setCompanyShown(true);
  }, [setCompanyShown, general]);
}

/** Names the detail part of the window title while the calling page is mounted. */
export function useWindowTitleDetail(detail: string | null | undefined) {
  const setDetail = useContext(WindowTitleDetailContext);
  const value = detail?.trim() || null;
  useEffect(() => {
    if (!setDetail) return;
    setDetail(value);
    return () => setDetail(null);
  }, [setDetail, value]);
}
